// components/VideoPlayer/SubtitleSettings/EmbeddedSubtitlesModal.jsx
import React, { useState, useEffect, useCallback } from "react";
import useEmbeddedSubtitles, {
  getLanguageName,
} from "../../../hooks/useEmbeddedSubtitles";
import { useVideo } from "../../../context/VideoContext";
import { useSelectedSubtitles } from "../../../context/SelectedSubtitlesContext";
import useVideoManager from "../../../hooks/useVideoManager";
import { useNotification } from "../../../context/NotificationContext";
import { isEmbeddedCacheValid, setEmbeddedCache, getEmbeddedCache } from "../../../utils/embeddedSubtitlesCache";
import srtParser2 from "srt-parser-2";
import "./EmbeddedSubtitlesModal.css";

const EmbeddedSubtitlesModal = ({ onClose }) => {
  const { videoFile, videoMetadata } = useVideo(); // 🎯 اضافه کردن videoMetadata
  const {
    isLoading,
    loadingMessage,
    getEmbeddedSubtitles,
    extractSubtitleContent,
  } = useEmbeddedSubtitles();
  const { loadLocalSubtitle } = useVideoManager();
  const { showInfo } = useNotification();
  const { addToHistory } = useSelectedSubtitles(); // 🎯 اضافه کردن addToHistory

  const [subtitles, setSubtitles] = useState([]);
  const [selectedForDownload, setSelectedForDownload] = useState({
    1: null,
    2: null,
  });
  const [error, setError] = useState(null);
  const [step, setStep] = useState("idle");
  const [isAdding, setIsAdding] = useState(false);

  const videoFileName = videoFile?.name || videoMetadata?.fileName || "";

  useEffect(() => {
    const extractSubtitles = async () => {
      if (!videoFile) {
        setError("فایل ویدیو در دسترس نیست");
        setStep("error");
        return;
      }

      if (isEmbeddedCacheValid(videoFileName)) {
        setSubtitles(getEmbeddedCache().list);
        setStep("done");
        return;
      }

      try {
        setStep("analyzing");
        setError(null);
        const embeddedSubs = await getEmbeddedSubtitles(videoFile);
        setSubtitles(embeddedSubs);
        setEmbeddedCache(embeddedSubs, videoFileName);
        setStep("done");
      } catch (err) {
        console.error("Error extracting subtitles:", err);
        setError("خطا در شناسایی زیرنویس‌ها");
        setStep("error");
      }
    };

    extractSubtitles();
  }, [videoFile, videoFileName, getEmbeddedSubtitles]);

  const handleSelectSubtitle = (subtitle, targetNumber) => {
    setSelectedForDownload((prev) => {
      if (prev[targetNumber]?.index === subtitle.index) {
        return { ...prev, [targetNumber]: null };
      }
      return { ...prev, [targetNumber]: subtitle };
    });
  };

  const isSubtitleSelected = (subtitle, targetNumber) => {
    return selectedForDownload[targetNumber]?.index === subtitle.index;
  };

  // ========== تابع افزودن زیرنویس‌ها ==========
  const handleAddSubtitles = useCallback(async () => {
    const selectedSubs = Object.entries(selectedForDownload).filter(
      ([_, sub]) => sub !== null,
    );

    if (selectedSubs.length === 0) {
      showInfo("لطفاً حداقل یک زیرنویس انتخاب کنید", {
        type: "warning",
        duration: 2000,
      });
      return;
    }

    setIsAdding(true);

    try {
      for (const [subNumber, subtitle] of selectedSubs) {
        const targetNumber = parseInt(subNumber);
        const trackIndex = subtitles.indexOf(subtitle);

        if (trackIndex === -1) continue;

        showInfo(`در حال استخراج زیرنویس ${targetNumber}...`, {
          duration: 1500,
        });

        const subtitleContent = await extractSubtitleContent(
          videoFile,
          trackIndex,
        );

        // استخراج cues
        const parser = new srtParser2();
        const cues = parser.fromSrt(subtitleContent);

        const language =
          subtitle.language === "fas" || subtitle.language === "per"
            ? "fa"
            : subtitle.language;

        // اطلاعات کامل برای ذخیره در context
        const subtitleData = {
          sourceType: "embedded",
          language: language,
          title: subtitle.title,
          format: subtitle.format,
          cues: cues,
          rawContent: subtitleContent,
          nameOfMovie: videoMetadata?.filmName || "نامشخص",
          videoType: videoMetadata?.videoType || "movie",
          season: videoMetadata?.season || "",
          episode: videoMetadata?.episode || "",
          movieFileName: videoMetadata?.fileName,
          trackIndex: trackIndex,
        };

        // اضافه کردن به تاریخچه context
        addToHistory(subtitleData, targetNumber);

        const subtitleBlob = new Blob([subtitleContent], {
          type: "text/plain",
        });
        const subtitleFile = new File(
          [subtitleBlob],
          `embedded_subtitle_${trackIndex}_${targetNumber}.srt`,
          { type: "text/plain" },
        );

        await loadLocalSubtitle(
          subtitleFile,
          language,
          targetNumber,
          subtitleData,
        );

        // ذخیره در localStorage برای استفاده‌های بعدی
        try {
          const embeddedHistory = JSON.parse(
            localStorage.getItem("embeddedSubtitlesHistory") || "[]",
          );
          embeddedHistory.push({
            ...subtitleData,
            extractedAt: new Date().toISOString(),
          });
          localStorage.setItem(
            "embeddedSubtitlesHistory",
            JSON.stringify(embeddedHistory.slice(-20)),
          );
        } catch (storageError) {
          console.warn("Could not save to localStorage:", storageError);
        }
      }

      showInfo("✅ زیرنویس‌های انتخاب شده با موفقیت اضافه شدند", {
        type: "success",
        duration: 3000,
      });

      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {
      console.error("Error adding subtitles:", error);
      showInfo("❌ خطا در افزودن زیرنویس‌ها: " + error.message, {
        type: "error",
        duration: 4000,
      });
    } finally {
      setIsAdding(false);
    }
  }, [
    selectedForDownload,
    subtitles,
    videoFile,
    videoMetadata,
    extractSubtitleContent,
    loadLocalSubtitle,
    showInfo,
    onClose,
    addToHistory,
  ]);

  const getFormatColor = (format) => {
    switch (format) {
      case "srt":
        return { bg: "rgba(33, 150, 243, 0.2)", text: "#42a5f5", label: "SRT" };
      case "ass":
        return { bg: "rgba(244, 67, 54, 0.2)", text: "#ef5350", label: "ASS" };
      case "vtt":
        return { bg: "rgba(76, 175, 80, 0.2)", text: "#66bb6a", label: "VTT" };
      default:
        return {
          bg: "rgba(255,255,255,0.08)",
          text: "rgba(255,255,255,0.6)",
          label: format?.toUpperCase() || "?",
        };
    }
  };

  return (
    <div
      className="embedded-subtitles-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="embedded-subtitles-panel">
        <div className="embedded-subtitles-header">
          <h3>🎬 زیرنویس‌های داخلی فیلم</h3>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="embedded-subtitles-content">
          {step === "analyzing" && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p className="loading-message">
                {loadingMessage || "در حال بررسی..."}
              </p>
            </div>
          )}

          {step === "error" && (
            <div className="error-state">
              <span className="error-icon">⚠️</span>
              <p className="error-message">{error}</p>
              <button className="retry-button" onClick={onClose}>
                بازگشت
              </button>
            </div>
          )}

          {step === "done" && subtitles.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon">📭</span>
              <p className="empty-text">این فیلم زیرنویس داخلی ندارد</p>
            </div>
          )}

          {step === "done" && subtitles.length > 0 && (
            <div className="subtitles-list-section">
              <div className="subtitles-info">
                <span className="subtitles-count">
                  {subtitles.length} زیرنویس پیدا شد
                </span>
              </div>

              <div className="subtitles-list">
                {subtitles.map((sub, index) => {
                  const formatInfo = getFormatColor(sub.format);
                  return (
                    <div
                      key={sub.index || index}
                      className="embedded-subtitle-item"
                    >
                      <div className="subtitle-info-section">
                        <span className="subtitle-language-badge">
                          {getLanguageName(sub.language)}
                        </span>
                        <span className="subtitle-title-text">{sub.title}</span>
                        <span
                          className="subtitle-format-badge"
                          style={{
                            background: formatInfo.bg,
                            color: formatInfo.text,
                          }}
                        >
                          {formatInfo.label}
                        </span>
                      </div>
                      <div className="subtitle-actions">
                        <button
                          className={`selection-btn btn-1 ${
                            isSubtitleSelected(sub, 1) ? "selected" : ""
                          }`}
                          onClick={() => handleSelectSubtitle(sub, 1)}
                          disabled={isSubtitleSelected(sub, 2) || isAdding}
                        >
                          ۱
                        </button>
                        <button
                          className={`selection-btn btn-2 ${
                            isSubtitleSelected(sub, 2) ? "selected" : ""
                          }`}
                          onClick={() => handleSelectSubtitle(sub, 2)}
                          disabled={isSubtitleSelected(sub, 1) || isAdding}
                        >
                          ۲
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {(selectedForDownload[1] || selectedForDownload[2]) &&
          step === "done" && (
            <div className="embedded-subtitles-footer">
              <div className="selected-info">
                {selectedForDownload[1] && (
                  <span className="selected-badge">
                    ① {getLanguageName(selectedForDownload[1].language)}
                  </span>
                )}
                {selectedForDownload[2] && (
                  <span className="selected-badge">
                    ② {getLanguageName(selectedForDownload[2].language)}
                  </span>
                )}
              </div>
              <button
                className="add-subtitles-btn"
                onClick={handleAddSubtitles}
                disabled={isAdding}
              >
                {isAdding ? (
                  <>
                    <span className="button-spinner"></span>
                    در حال افزودن...
                  </>
                ) : (
                  "افزودن زیرنویس‌ها"
                )}
              </button>
            </div>
          )}
      </div>
    </div>
  );
};

export default EmbeddedSubtitlesModal;
